import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, X, DoorOpen, ExternalLink, User, UserPlus, ArrowLeftRight, CalendarClock } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import rentalsService from '../services/rentalsService';
import RentalToolsModal from '../components/RentalToolsModal';
import RentalLeaseSection from '../components/RentalLeaseSection';
import GateAccessSection from '../components/GateAccessSection';
import { toLastFirst, byLastName } from '../utils/customerName';
import { toast } from 'sonner';

const RentalForm = () => {
  const { facilityId } = useFacility();
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Tenant kind for a NEW rental (owner, 2026-08-10): existing tenant
  // (dropdown of current customers) or a brand-new tenant created inline
  // right here — the Customers page no longer has Add Existing Tenant.
  // Preselected by the Rentals page's New Rental menu (?tenant=new).
  const [tenantMode, setTenantMode] = useState(
    searchParams.get('tenant') === 'new' ? 'new' : 'existing',
  );
  const [newTenant, setNewTenant] = useState({
    first_name: '', last_name: '', email: '', phone: '', opt_in_sms: true,
  });
  const setNT = (k) => (e) =>
    setNewTenant({
      ...newTenant,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });
  const [loading, setLoading] = useState(false);
  // `loading` only ever guarded the SAVE button. In edit mode the form
  // rendered instantly with blank defaults, so until the fetch landed it was
  // indistinguishable from "New Rental" — invisible on a desktop connection,
  // plainly wrong on a phone. Worse, if the fetch FAILED the blank form
  // stayed editable and saving would have overwritten the rental with empty
  // values.
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [loadError, setLoadError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  // Derived "billed through" date (read-only; from the rental GET) — shown
  // beside Paid Through so the operator sees the arrears window.
  const [billedTo, setBilledTo] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    unit_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    paid_to: '',
    status: 'Active',
    monthly_rate: '',
    balance: 0,
    initial_balance: 0,
    notes: ''
  });

  const fetchData = useCallback(async () => {
    if (!facilityId) return;
    setLoadError(null);
    try {
      const reqs = [
        // status=all: the customers endpoint defaults to status="active"
        // (customers who ALREADY have a non-Ended rental), which excludes
        // brand-new customers — exactly the ones you create a first rental
        // for. Fetch every non-archived customer so they're selectable.
        // Trailing slashes — the no-slash form draws a 307 redirect and
        // SAFARI STRIPS Authorization on redirects: this exact pair made
        // aging-drill → rental editor read "Not authenticated" on every
        // iPhone tap (2026-08-02).
        apiClient.get(`/api/facilities/${facilityId}/customers/?status=all`),
        apiClient.get(`/api/facilities/${facilityId}/units/`),
      ];
      if (isEditMode) {
        reqs.push(apiClient.get(`/api/facilities/${facilityId}/rentals/${id}`));
      }
      const [customersRes, unitsRes, rentalRes] = await Promise.all(reqs);
      setCustomers(customersRes.data);

      const available = unitsRes.data.filter((u) => u.status === 'Available');
      if (isEditMode && rentalRes) {
        const r = rentalRes.data;
        // The rental's current unit is Occupied, so it's excluded by the
        // Available filter — add it back so it stays selectable/visible.
        const currentUnit = unitsRes.data.find((u) => u._id === r.unit_id);
        setUnits(
          currentUnit && !available.some((u) => u._id === currentUnit._id)
            ? [currentUnit, ...available]
            : available
        );
        // The rental's persisted end-date is either `end_date` (older
        // path) or `ended_at` (newer; stamped by /end and by the legacy
        // wizard finalize flow). Surface whichever exists, normalized to
        // YYYY-MM-DD so the <input type="date"> accepts it.
        const moveOutRaw = r.end_date || r.ended_at || '';
        setFormData({
          customer_id: r.customer_id || '',
          unit_id: r.unit_id || '',
          start_date: (r.start_date || '').split('T')[0]
            || new Date().toISOString().split('T')[0],
          end_date: moveOutRaw ? moveOutRaw.split('T')[0] : '',
          // "Paid through" coverage date — auto-advanced by the payments
          // path, but operator-editable here. Normalize to YYYY-MM-DD.
          paid_to: r.paid_to ? String(r.paid_to).split('T')[0] : '',
          status: r.status || 'Active',
          monthly_rate: r.monthly_rate ?? '',
          balance: r.balance ?? 0,
          notes: r.notes || '',
        });
        setBilledTo(r.billed_to ? String(r.billed_to).split('T')[0] : '');
      } else {
        setUnits(available);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Surface it. Swallowing this left an editable BLANK form in edit
      // mode, which saves as a wipe.
      if (isEditMode) {
        setLoadError(
          error?.response?.data?.detail || 'Could not load this rental.');
      }
    } finally {
      setInitialLoading(false);
    }
  }, [facilityId, id, isEditMode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'unit_id' && value) {
      const selectedUnit = units.find(u => u._id === value);
      if (selectedUnit) {
        setFormData(prev => ({ ...prev, monthly_rate: selectedUnit.monthly_rate }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...formData,
      monthly_rate: parseFloat(formData.monthly_rate),
      balance: parseFloat(formData.balance || 0),
    };
    // New-tenant path: create the customer first, then the rental under
    // the freshly-minted id. Address fields are intentionally blank —
    // the profile page collects them later.
    if (!isEditMode && tenantMode === 'new') {
      const first = newTenant.first_name.trim();
      const last = newTenant.last_name.trim();
      if (!first || !last) {
        alert('First and last name are required for a new tenant.');
        setLoading(false);
        return;
      }
      try {
        const res = await apiClient.post(`/api/facilities/${facilityId}/customers/`, {
          first_name: first,
          last_name: last,
          full_name: `${first} ${last}`,
          email: newTenant.email.trim(),
          phone: newTenant.phone.trim(),
          opt_in_sms: newTenant.opt_in_sms,
          address: '', city: '', state: '', zip_code: '',
        });
        payload.customer_id = res.data._id;
      } catch (error) {
        alert(error?.response?.data?.detail || 'Could not create the tenant.');
        setLoading(false);
        return;
      }
    }
    // Empty Move-out clears the persisted end_date (operator un-ends a
    // rental). Backend `exclude_unset` preserves explicit nulls. We
    // send null rather than empty string so the PUT path $unsets the
    // field cleanly instead of stamping a malformed empty-string date.
    payload.end_date = formData.end_date ? formData.end_date : null;
    // Same null-clear semantics for the paid-through date.
    payload.paid_to = formData.paid_to ? formData.paid_to : null;
    try {
      if (isEditMode) {
        await apiClient.put(
          `/api/facilities/${facilityId}/rentals/${id}`,
          payload
        );
        // Stay on the rental being edited.
        toast.success('Rental saved.');
      } else {
        await apiClient.post(
          `/api/facilities/${facilityId}/rentals/`,
          payload
        );
        navigate('/rentals');
      }
    } catch (error) {
      console.error('Error saving rental:', error);
      alert(
        error?.response?.data?.detail
        || `Failed to ${isEditMode ? 'update' : 'create'} rental`
      );
    } finally {
      setLoading(false);
    }
  };

  // Move-out from the rental page. Uses the SAME rentalsService.end call and
  // the same 409 force-path as the customer's inline row — two surfaces doing
  // this differently is how they drift.
  const [endingRental, setEndingRental] = useState(false);
  // Transfer / plan. Same component the customer's rentals row
  // mounts, so the two surfaces run one implementation.
  const [toolModal, setToolModal] = useState(null);
  const openTool = (kind) => setToolModal({
    kind,
    rental: {
      _id: id,
      unit_number: formData.unit_number,
      balance: parseFloat(formData.balance) || 0,
    },
  });
  const moveOut = async () => {
    if (!facilityId || !id) return;
    const unitNo = formData.unit_number || '—';
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Move out unit ${unitNo}? This ends the rental and frees the unit.`,
    )) return;
    setEndingRental(true);
    try {
      await rentalsService.end(facilityId, id, { force: false });
      navigate('/rentals');
    } catch (err) {
      if (err?.response?.status === 409) {
        const detail = err?.response?.data?.detail
          || 'This rental has an outstanding balance or credit.';
        // A lien 409 asks a different question from a balance 409 — closing a
        // legal process needs its own explicit yes.
        if (/lien/i.test(detail)) {
          // eslint-disable-next-line no-alert
          if (window.confirm(
            `${detail}

Close the lien workflow and move this tenant out?`,
          )) {
            try {
              await rentalsService.end(facilityId, id,
                { force: true, closeLien: true });
              navigate('/rentals');
              return;
            } catch (e3) {
              // eslint-disable-next-line no-alert
              alert(e3?.response?.data?.detail || 'Could not move out.');
            }
          }
          setEndingRental(false);
          return;
        }
        // eslint-disable-next-line no-alert
        if (window.confirm(`${detail}

Force the move-out anyway?`)) {
          try {
            await rentalsService.end(facilityId, id, { force: true });
            navigate('/rentals');
            return;
          } catch (e2) {
            // eslint-disable-next-line no-alert
            alert(e2?.response?.data?.detail || 'Could not move out.');
          }
        }
      } else {
        // eslint-disable-next-line no-alert
        alert(err?.response?.data?.detail || 'Could not move out.');
      }
    } finally {
      setEndingRental(false);
    }
  };

  // Never render an editable form over data that has not arrived.
  if (isEditMode && initialLoading) {
    return (
      <div className="py-16 text-center text-[13px] text-slate-400">
        Loading rental…
      </div>
    );
  }
  if (isEditMode && loadError) {
    return (
      <div className="py-16 text-center">
        <p className="text-[13px] text-rose-600">{loadError}</p>
        <button type="button" onClick={() => navigate('/rentals')}
          className="mt-3 text-[13px] text-blue-600 hover:underline">
          Back to rentals
        </button>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all";
  const labelClass = "block text-[13px] font-medium text-slate-700 mb-1.5";

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{isEditMode ? 'Edit Rental' : 'New Rental'}</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">{isEditMode ? 'Update this rental agreement' : 'Create a new rental agreement'}</p>
        </div>
        {/* Landing here from a unit or a customer used to be a dead end — the
            page had a title and nothing else, so opening the unit, the tenant
            or moving out meant navigating back out to the customer row. */}
        {isEditMode && (
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            {formData.unit_id && (
              <button type="button" onClick={() => navigate(`/units/${formData.unit_id}`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700">
                <ExternalLink className="h-3.5 w-3.5" /> Unit
              </button>
            )}
            {formData.customer_id && (
              <button type="button" onClick={() => navigate(`/customers/${formData.customer_id}/edit`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700">
                <User className="h-3.5 w-3.5" /> Customer
              </button>
            )}
            <button type="button" onClick={() => openTool('transfer')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700">
              <ArrowLeftRight className="h-3.5 w-3.5" /> Transfer
            </button>
            <button type="button" onClick={() => openTool('plan')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700">
              <CalendarClock className="h-3.5 w-3.5" /> Plan
            </button>
            {/* Move Out stays rose — destructive, must never look like the
                navigation actions beside it. Solid per the button standard. */}
            <button type="button" onClick={moveOut} disabled={endingRental}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3 sm:text-[13px] rounded-lg bg-rose-600 font-medium text-white hover:bg-rose-700 disabled:opacity-50">
              <DoorOpen className="h-3.5 w-3.5" />
              {endingRental ? 'Moving out…' : 'Move Out'}
            </button>
          </div>
        )}
      </div>

      <RentalToolsModal
        facilityId={facilityId}
        toolModal={toolModal}
        onClose={() => setToolModal(null)}
        onChanged={() => window.location.reload()}
      />

      {isEditMode && facilityId && id && (
        <div className="mb-4">
          <RentalLeaseSection facilityId={facilityId} rentalId={id} />
          <GateAccessSection
            facilityId={facilityId}
            rentalId={id}
            customer={customers.find((c) => c._id === formData.customer_id)}
            unit={units.find((u) => u._id === formData.unit_id)}
          />
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        <form onSubmit={handleSubmit} className="space-y-5">
          {!isEditMode && (
            <div className="flex gap-1.5">
              {[
                { key: 'existing', label: 'Existing tenant', Icon: User },
                { key: 'new', label: 'New tenant', Icon: UserPlus },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTenantMode(key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                    tenantMode === key
                      ? 'bg-blue-600 text-white'
                      : 'border border-slate-300 bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          )}

          {(isEditMode || tenantMode === 'existing') ? (
            <div>
              <label className={labelClass}>Customer *</label>
              <select name="customer_id" required value={formData.customer_id} onChange={handleChange} className={inputClass}>
                <option value="">Select a customer</option>
                {[...customers]
                  .sort(byLastName)
                  .map((customer) => (
                    <option key={customer._id} value={customer._id}>
                      {toLastFirst(customer.full_name)}{customer.email ? ` - ${customer.email}` : ''}
                    </option>
                  ))}
              </select>
              {customers.length === 0 && (
                <p className="text-[11px] text-red-500 mt-1">No customers found. Please add a customer first.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>First name *</label>
                <input value={newTenant.first_name} onChange={setNT('first_name')} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Last name *</label>
                <input value={newTenant.last_name} onChange={setNT('last_name')} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" value={newTenant.email} onChange={setNT('email')} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input value={newTenant.phone} onChange={setNT('phone')} className={inputClass} />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-[13px] text-slate-600 select-none">
                <input type="checkbox" className="rounded border-slate-300" checked={newTenant.opt_in_sms} onChange={setNT('opt_in_sms')} />
                Tenant consents to SMS notices
              </label>
            </div>
          )}

          <div>
            <label className={labelClass}>Unit *</label>
            <select name="unit_id" required value={formData.unit_id} onChange={handleChange} className={inputClass}>
              <option value="">Select a unit</option>
              {Object.entries(
                units.reduce((acc, u) => {
                  const t = u.unit_type || 'Other';
                  (acc[t] = acc[t] || []).push(u);
                  return acc;
                }, {}),
              )
                .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                .map(([type, list]) => (
                  <optgroup key={type} label={type}>
                    {list
                      .sort((a, b) => String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true }))
                      .map((unit) => (
                        <option key={unit._id} value={unit._id}>
                          {unit.unit_number} - {unit.unit_type} (${unit.monthly_rate}/month)
                        </option>
                      ))}
                  </optgroup>
                ))}
            </select>
            {units.length === 0 && (
              <p className="text-[11px] text-red-500 mt-1">No available units.</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Start Date *</label>
              <input type="date" name="start_date" required value={formData.start_date} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Monthly Rate *</label>
              <input type="number" name="monthly_rate" required step="0.01" value={formData.monthly_rate} onChange={handleChange} className={inputClass} placeholder="0.00" />
            </div>
            {/* The field said "Initial Balance" but was bound to `balance`.
                It therefore SHOWED the live balance under the wrong name and,
                if typed in, overwrote it directly — bypassing the ledger and
                breaking `balance == Σ(unpaid invoices)`, the identity the
                delinquency queue, aging donut, KPIs and late-fee cascade all
                depend on.

                On CREATE it is a real carried-over balance, which mints an
                `opening_balance` invoice (see routers/rentals.py). On EDIT
                the balance is DERIVED from the ledger, so it is shown
                read-only — change it by invoicing, paying, refunding or
                writing off, never by typing. */}
            {!isEditMode ? (
              <div>
                <label className={labelClass}>Carried-over balance</label>
                <input type="number" name="initial_balance" step="0.01"
                       value={formData.initial_balance} onChange={handleChange}
                       className={inputClass} placeholder="0.00" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Only for a tenant migrated from another system — it creates
                  an opening-balance invoice. Leave 0 for a new rental.
                </p>
              </div>
            ) : (
              <div>
                <label className={labelClass}>Balance</label>
                <input type="number" value={formData.balance} disabled readOnly
                       className={`${inputClass} bg-slate-50 text-slate-500 cursor-not-allowed`} />
                <p className="text-[11px] text-slate-400 mt-1">
                  Derived from the ledger — invoices minus payments. Change it
                  by invoicing, taking a payment, refunding or writing off.
                </p>
              </div>
            )}
          </div>

          {isEditMode && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
                  <option value="Active">Active</option>
                  <option value="Delinquent">Delinquent</option>
                  <option value="Lien">Lien</option>
                  <option value="Ended">Ended</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Bill To</label>
                <input
                  type="date"
                  value={billedTo}
                  readOnly
                  disabled
                  className={`${inputClass} bg-slate-50 text-slate-500 cursor-not-allowed`}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Billed through (latest invoice). Read-only.
                </p>
              </div>
              <div>
                <label className={labelClass}>Paid Through</label>
                <input type="date" name="paid_to" value={formData.paid_to} onChange={handleChange} className={inputClass} />
                <p className="text-[11px] text-slate-400 mt-1">
                  Date rent is covered through. Auto-advances on payment; override here.
                </p>
              </div>
              <div>
                <label className={labelClass}>End Date</label>
                <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} className={inputClass} />
                <p className="text-[11px] text-slate-400 mt-1">
                  Set when tenant moves out. Leave blank to keep this rental open.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Any additional notes about this rental..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => navigate('/rentals')} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button type="submit" disabled={loading || ((isEditMode || tenantMode === 'existing') && customers.length === 0) || units.length === 0} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Save className="h-3.5 w-3.5" /> {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Rental')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RentalForm;
